import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faUser, faClock, faEnvelope, faPhone, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import api from '../../api/api';
import '../../styles/admin/RegistrationDetailPage.css';

const RegistrationDetailPage = () => {
  const { id: registrationId } = useParams();
  const navigate = useNavigate();
  
  // State
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  
  // Halaqa dropdown state
  const [halaqaId, setHalaqaId] = useState('');
  const [halaqas, setHalaqas] = useState([]);
  
  // Rejection modal state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Fetch registration details
  const fetchRegistrationDetails = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/registrations/${registrationId}`);
      
      if (response.data.success) {
        setRegistration(response.data.data);
      } else {
        alert('Failed to load registration details');
        navigate(-1);
      }
    } catch (error) {
      console.error('Error fetching registration details:', error);
      alert('Failed to load registration details');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch halaqas for the center
  const fetchHalaqas = async () => {
    try {
      const response = await api.get('/halaqas');
      
      if (response.data.success) {
        setHalaqas(response.data.data);
      } else {
        // Fallback mock data if API fails
        setHalaqas([
          { name: 'Morning Halaqa', _id: 'morning' },
          { name: 'Evening Halaqa', _id: 'evening' },
          { name: 'Weekend Halaqa', _id: 'weekend' }
        ]);
      }
    } catch (error) {
      console.error('Error fetching halaqas:', error);
      // Fallback mock data
      setHalaqas([
        { name: 'Morning Halaqa', _id: 'morning' },
        { name: 'Evening Halaqa', _id: 'evening' },
        { name: 'Weekend Halaqa', _id: 'weekend' }
      ]);
    }
  };
  
  // Handle approve registration
  const handleApprove = async () => {
    // Validate halaqa selection if registration has child
    if (registration.hasChild && !halaqaId) {
      alert('Please select a halaqa for the child');
      return;
    }
    
    if (window.confirm('Are you sure you want to approve this registration?')) {
      setProcessing(true);
      try {
        const response = await api.post(`/registrations/${registrationId}/approve`, {
          halaqaId
        });
        
        if (response.data.success) {
          alert('Registration approved successfully');
          navigate('/admin/registrations');
        } else {
          alert('Failed to approve registration');
        }
      } catch (error) {
        console.error('Error approving registration:', error);
        alert(error.response?.data?.message || 'Failed to approve registration');
      } finally {
        setProcessing(false);
      }
    }
  };
  
  // Show rejection modal
  const showRejectConfirmation = () => {
    setShowRejectModal(true);
  };
  
  // Handle reject registration
  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    
    setProcessing(true);
    try {
      const response = await api.post(`/registrations/${registrationId}/reject`, {
        rejectionReason
      });
      
      if (response.data.success) {
        setShowRejectModal(false);
        alert('Registration rejected');
        navigate('/admin/registrations');
      } else {
        alert('Failed to reject registration');
      }
    } catch (error) {
      console.error('Error rejecting registration:', error);
      alert(error.response?.data?.message || 'Failed to reject registration');
    } finally {
      setProcessing(false);
    }
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }) + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  // Initialize
  useEffect(() => {
    fetchRegistrationDetails();
    fetchHalaqas();
  }, [registrationId]);
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading registration details...</p>
      </div>
    );
  }
  
  if (!registration) {
    return (
      <div className="registration-detail-container">
        <div className="registration-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </button>
          <h1 className="header-title">Registration Details</h1>
          <div className="header-placeholder"></div>
        </div>
        <div className="error-container">
          <p className="error-text">Failed to load registration details</p>
          <button className="retry-button" onClick={fetchRegistrationDetails}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="registration-detail-container">
      <div className="registration-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back
        </button>
        <h1 className="header-title">Registration Details</h1>
        <div className="header-placeholder"></div>
      </div>
      
      <div className="registration-content">
        {/* Status Badge */}
        <div className={`status-badge-large ${registration.status}-badge`}>
          {registration.status.charAt(0).toUpperCase() + registration.status.slice(1)}
        </div>
        
        {/* Parent Information Section */}
        <div className="detail-section">
          <h2 className="section-title">Parent Information</h2>
          
          <div className="field-group">
            <div className="field-label">Name</div>
            <div className="field-value">{registration.parentName}</div>
          </div>
          
          <div className="field-group">
            <div className="field-label">
              <FontAwesomeIcon icon={faEnvelope} className="field-icon" /> Email
            </div>
            <div className="field-value">{registration.parentEmail}</div>
          </div>
          
          <div className="field-group">
            <div className="field-label">
              <FontAwesomeIcon icon={faPhone} className="field-icon" /> Phone
            </div>
            <div className="field-value">{registration.parentPhone}</div>
          </div>
          
          {registration.parentAddress && (
            <div className="field-group">
              <div className="field-label">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="field-icon" /> Address
              </div>
              <div className="field-value">{registration.parentAddress}</div>
            </div>
          )}
          
          {/* Custom fields if any */}
          {registration.customFields && Object.keys(registration.customFields).length > 0 && (
            <>
              {Object.entries(registration.customFields).map(([key, value]) => (
                <div key={key} className="field-group">
                  <div className="field-label">{key}</div>
                  <div className="field-value">{value}</div>
                </div>
              ))}
            </>
          )}
        </div>
        
        {/* Child Information Section (if has child) */}
        {registration.hasChild && registration.childInfo && (
          <div className="detail-section">
            <h2 className="section-title">Child Information</h2>
            
            <div className="field-group">
              <div className="field-label">
                <FontAwesomeIcon icon={faUser} className="field-icon" /> Name
              </div>
              <div className="field-value">{registration.childInfo.name}</div>
            </div>
            
            <div className="field-group">
              <div className="field-label">Grade</div>
              <div className="field-value">{registration.childInfo.grade}</div>
            </div>
            
            {registration.status === 'pending' && (
              <div className="halaqa-selection">
                <label htmlFor="halaqa-select" className="field-label">Assign to Halaqa</label>
                <select 
                  id="halaqa-select"
                  className="halaqa-dropdown"
                  value={halaqaId}
                  onChange={(e) => setHalaqaId(e.target.value)}
                  disabled={processing}
                >
                  <option value="">Select Halaqa</option>
                  {halaqas.map(halaqa => (
                    <option key={halaqa._id} value={halaqa._id}>
                      {halaqa.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
        
        {/* Submission Details */}
        <div className="detail-section">
          <h2 className="section-title">Submission Details</h2>
          
          <div className="field-group">
            <div className="field-label">
              <FontAwesomeIcon icon={faClock} className="field-icon" /> Submitted On
            </div>
            <div className="field-value">{formatDate(registration.createdAt)}</div>
          </div>
        </div>
        
        {/* Action Buttons (for pending registrations) */}
        {registration.status === 'pending' && (
          <div className="action-button-container">
            <button 
              className="action-button reject-button"
              onClick={showRejectConfirmation}
              disabled={processing}
            >
              Reject
            </button>
            
            <button 
              className="action-button approve-button"
              onClick={handleApprove}
              disabled={processing}
            >
              Approve
            </button>
          </div>
        )}
      </div>
      
      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <h2 className="modal-title">Reject Registration</h2>
            
            <textarea
              className="rejection-reason-input"
              placeholder="Please provide a reason for rejection"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              maxLength={500}
            />
            
            <div className="modal-button-container">
              <button 
                className="modal-button cancel-button"
                onClick={() => setShowRejectModal(false)}
                disabled={processing}
              >
                Cancel
              </button>
              
              <button 
                className="modal-button confirm-button"
                onClick={handleReject}
                disabled={processing || !rejectionReason.trim()}
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationDetailPage;